// Build the CID→location index from signed state (szzt-spec.md §9.2).
//
// Resolves a site target through @szzt/records, then re-derives every CID the chain pins and
// REQUIRES each to match — failing closed otherwise. Two distinct kinds of pin are checked, mirroring
// the reference verifier (`@szzt/verify` verify()) check-for-check so the gateway and `szzt verify`
// can never disagree about a site:
//   - the STRONG-REF pins: each record's recomputed atproto CID vs. the CID the referrer pinned
//     (`site.current.cid`, `deployment.tile.cid`) — these bind the chain to specific record VERSIONS,
//     so following a strong ref by URI alone cannot land on a silently-rolled-back older record.
//   - the ROOT pin: dagCbor(inline MASL) vs. the manifest record's `cid` and the deployment `root`.
// This is what §9.2 means by "derive every CID it fetches from the signed record only": the resulting
// index maps each resource path and each bare content CID to a location the gateway has verifiably
// resolved, so it can never serve a CID it did not derive from signed state.

import { dagCbor, MAX_FILE_BYTES, MAX_FILE_COUNT, MAX_TOTAL_BYTES } from "@szzt/core";
import {
  parseAtUri,
  readCidField,
  readLink,
  readStrongRef,
  recomputeRecordCid,
  resolveTarget,
  type RepoReader,
} from "@szzt/records";
import { GatewayError } from "./types.ts";
import type { CidLocation, SzztIndex, ResourceLocation } from "./types.ts";

/** Resolve `target` (a site / deployment / manifest AT-URI) to a serving index, verifying the root CID. */
export async function resolveIndex(reader: RepoReader, target: string): Promise<SzztIndex> {
  const resolved = await resolveTarget(reader, target);
  const masl = resolved.manifest.value.tile;
  // Blobs live in the repo that holds the manifest record.
  const did = parseAtUri(resolved.manifest.ref.uri).did;

  // Re-derive the strong-ref CID pins and require each to match (§9.2). `resolveTarget` follows these
  // refs by URI only, so without these two checks a repo whose `current.uri`/`tile.uri` still resolve
  // but now hold an OLDER, self-consistent record would serve as if current: every root check below
  // passes (the old deployment pins its own old manifest and root), and only the pinned atproto CIDs
  // reveal the rollback. Checked in chain order, site → deployment → manifest. Fail closed on mismatch.
  if (resolved.site && resolved.deployment) {
    const pinned = readStrongRef(resolved.site.ref.value, "current", "app.szzt.site").cid;
    const recomputed = await recomputeRecordCid(resolved.deployment.ref.value);
    if (recomputed !== pinned) {
      throw new GatewayError(
        `deployment record CID mismatch: recomputed ${recomputed} != site current.cid ${pinned}`,
      );
    }
  }
  if (resolved.deployment) {
    const pinned = readStrongRef(resolved.deployment.ref.value, "tile", "app.szzt.deployment").cid;
    const recomputed = await recomputeRecordCid(resolved.manifest.ref.value);
    if (recomputed !== pinned) {
      throw new GatewayError(
        `manifest record CID mismatch: recomputed ${recomputed} != deployment tile.cid ${pinned}`,
      );
    }
  }

  // Re-derive the root CID and require it to match the signed pins (§9.2). Fail closed on mismatch.
  // The manifest record's `cid` is a plain string per the upstream lexicon (the pre-2026-10 `{$link}`
  // form is also accepted — records are immutable, so both live in repos).
  const { cid: rootCid, bytes: rootBytes } = await dagCbor(masl);
  const maslCidField = readCidField(resolved.manifest.ref.value, "cid", "ing.dasl.masl");
  if (rootCid !== maslCidField) {
    throw new GatewayError(
      `root CID mismatch: recomputed ${rootCid} != manifest record cid ${maslCidField}`,
    );
  }
  if (resolved.deployment) {
    const depRoot = readLink(resolved.deployment.ref.value, "root", "app.szzt.deployment");
    if (rootCid !== depRoot) {
      throw new GatewayError(`root CID mismatch: recomputed ${rootCid} != deployment root ${depRoot}`);
    }
  }

  // §3.1 ceilings, fail-closed. The signed manifest is authentic (every CID pin above matched) but its
  // CONTENTS are publisher-controlled, so a non-conforming publisher can declare more files, a larger
  // file, or more total bytes than §3.1 permits. `zip.ts assembleZip()` OMITS ZIP64 *because* it trusts
  // these bounds hold (entry count << 65535, total << 4 GiB), so this is the single gate that makes that
  // trust safe — reject an over-ceiling index here rather than let it reach the export path. Bounds are
  // the §3.1 constants, imported from @szzt/core so the gateway and the builder share one definition.
  // The `/` entry aliases the entry document (§3.3), so it is excluded from the count and byte total to
  // avoid double-counting that one resource. (Sizes are the manifest's declared `src.size`, i.e. signed
  // publisher claims — the same values the index and export downstream rely on.)
  let fileCount = 0;
  let totalBytes = 0;
  for (const [path, entry] of Object.entries(masl.resources)) {
    if (path === "/") continue;
    fileCount += 1;
    const size = entry.src.size;
    if (size > MAX_FILE_BYTES) {
      throw new GatewayError(
        `resource "${path}" is ${size} bytes, exceeds §3.1 per-file ceiling ${MAX_FILE_BYTES}`,
      );
    }
    totalBytes += size;
  }
  if (fileCount > MAX_FILE_COUNT) {
    throw new GatewayError(`file count ${fileCount} exceeds §3.1 ceiling ${MAX_FILE_COUNT}`);
  }
  if (totalBytes > MAX_TOTAL_BYTES) {
    throw new GatewayError(`total ${totalBytes} bytes exceeds §3.1 ceiling ${MAX_TOTAL_BYTES}`);
  }

  const resources = new Map<string, ResourceLocation>();
  const cidToLocation = new Map<string, CidLocation>();
  for (const [path, entry] of Object.entries(masl.resources)) {
    const cid = readLink(entry.src, "ref", `resource "${path}" src`);
    const contentType = entry["content-type"];
    const size = entry.src.size;
    const loc: ResourceLocation = {
      path,
      cid,
      size,
      contentType,
      ...(entry["content-security-policy"] !== undefined
        ? { csp: entry["content-security-policy"] }
        : {}),
      ...(entry.sourcemap !== undefined ? { sourcemap: entry.sourcemap } : {}),
    };
    resources.set(path, loc);
    // Resource CID → blob location. Content-addressed, so identical bytes at several paths dedup to
    // one entry (first path wins as the representative; any path still serves the same verified bytes).
    if (!cidToLocation.has(cid)) {
      cidToLocation.set(cid, { kind: "blob", did, cid, size, contentType, path });
    }
  }
  // The root CID resolves to the canonical dCBOR42 manifest bytes, not a blob (§9.2).
  cidToLocation.set(rootCid, { kind: "root", cid: rootCid, bytes: rootBytes });

  return {
    did,
    rootCid,
    name: masl.name,
    ...(resolved.deployment?.value.entry !== undefined
      ? { entry: resolved.deployment.value.entry }
      : {}),
    ...(masl.routing !== undefined ? { routing: masl.routing } : {}),
    resources,
    cidToLocation,
    ...(resolved.site?.value.hints !== undefined ? { hints: resolved.site.value.hints } : {}),
  };
}
